/**
 * DLA PRODUCT — assets/dla-product.js
 * Progressive enhancement only. The native Shopify product form works without
 * this file (default variant + default quantity are rendered server-side).
 *
 * IMPORTANT: This script NEVER changes checkout prices. It only updates the
 * quantity and variant id submitted through Shopify's native product form.
 * Bundle pricing must be configured with Shopify automatic discounts or a
 * compatible bundle app.
 *
 * No external libraries. Scoped per-section; safe with multiple instances and
 * with the Theme Editor (shopify:section:load).
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  function formatMoney(cents, currency, locale) {
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function initSection(root) {
    if (!root || root.dataset.dlaInit === 'true') return;
    root.dataset.dlaInit = 'true';

    var currency = root.dataset.currency;
    var locale = root.dataset.locale;

    var form = root.querySelector('[data-dla-form]');
    var idInput = root.querySelector('[data-dla-variant-id]');
    var qtyInput = root.querySelector('[data-dla-quantity]');
    var atcBtn = root.querySelector('[data-dla-atc]');
    var atcText = root.querySelector('[data-dla-atc-text]');
    var priceEl = root.querySelector('[data-dla-price]');
    var compareEl = root.querySelector('[data-dla-compare]');
    var saveEl = root.querySelector('[data-dla-save]');
    var stickyBar = root.querySelector('[data-dla-stickybar]');
    var stickyPrice = root.querySelector('[data-dla-sticky-price]');
    var stickyAtc = root.querySelector('[data-dla-sticky-atc]');

    /* ---------------- Bundle selector → native form quantity --------------- */

    var bundleRadios = root.querySelectorAll('[data-dla-bundle]');

    function applyBundle(radio) {
      if (!qtyInput || !radio) return;
      qtyInput.value = radio.dataset.quantity || '1';
    }

    bundleRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        applyBundle(radio);
      });
    });

    // Sync on load in case Liquid default and checked radio ever diverge,
    // or the default option was disabled in the Theme Editor.
    var checked = root.querySelector('[data-dla-bundle]:checked');
    if (!checked && bundleRadios.length) {
      bundleRadios[0].checked = true;
      checked = bundleRadios[0];
    }
    if (checked) applyBundle(checked);

    /* ------------------------- Variant selection --------------------------- */

    var variants = null;
    var variantsJson = root.querySelector('[data-dla-variants]');
    if (variantsJson) {
      try {
        variants = JSON.parse(variantsJson.textContent);
      } catch (e) {
        variants = null;
      }
    }

    var optionSelects = Array.prototype.slice.call(
      root.querySelectorAll('[data-dla-option]')
    );

    function currentVariant() {
      if (!variants) return null;
      var selected = optionSelects.map(function (s) { return s.value; });
      return variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === selected[i]; });
      }) || null;
    }

    function updateVariantUI() {
      var variant = currentVariant();
      var available = variant ? variant.available : false;

      if (variant && idInput) idInput.value = variant.id;

      if (priceEl && variant) {
        priceEl.textContent = formatMoney(variant.price, currency, locale);
      }
      if (stickyPrice && variant) {
        stickyPrice.textContent = formatMoney(variant.price, currency, locale);
      }
      var onSale = !!(variant && variant.compare_at_price > variant.price);

      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(variant.compare_at_price, currency, locale);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (saveEl) {
        if (onSale) {
          var pct = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          saveEl.textContent = 'Save ' + pct + '%';
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }

      if (atcBtn) {
        atcBtn.disabled = !available;
        if (atcText) {
          atcText.textContent = variant
            ? (available ? 'ADD TO CART' : 'SOLD OUT')
            : 'UNAVAILABLE';
        }
      }
      if (stickyAtc) stickyAtc.disabled = !available;

      if (variant && variant.featured_media && variant.featured_media.id) {
        scrollToMedia(String(variant.featured_media.id));
      }

      if (variant && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionSelects.forEach(function (select) {
      select.addEventListener('change', updateVariantUI);
    });

    /* ------------------------------ Gallery -------------------------------- */

    var track = root.querySelector('[data-dla-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-dla-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-dla-thumb]'));
    var prevBtn = root.querySelector('[data-dla-prev]');
    var nextBtn = root.querySelector('[data-dla-next]');

    function scrollToIndex(index) {
      if (!track || !slides.length) return;
      index = Math.max(0, Math.min(index, slides.length - 1));

      var target = slides[index].offsetLeft;
      var smooth = !REDUCED_MOTION.matches;

      track.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });

      /* Smooth scrolling is a silent no-op in some engines when the container
         also uses scroll-snap. Check back, and jump outright if nothing moved,
         so the slide always lands rather than the gallery appearing frozen. */
      if (!smooth) return;
      var startedAt = track.scrollLeft;
      setTimeout(function () {
        var moved = Math.abs(track.scrollLeft - startedAt) > 1;
        var arrived = Math.abs(track.scrollLeft - target) < 2;
        if (!moved && !arrived) {
          track.scrollLeft = target;
          /* The scroll listener syncs thumbs inside rAF, which can be throttled;
             sync here too so the active thumb never lags the visible slide. */
          syncThumbs();
        }
      }, 350);
    }

    function scrollToMedia(mediaId) {
      var index = slides.findIndex(function (s) { return s.dataset.mediaId === mediaId; });
      if (index > -1) scrollToIndex(index);
    }

    function activeIndex() {
      if (!track) return 0;
      var pos = track.scrollLeft + track.clientWidth / 2;
      for (var i = 0; i < slides.length; i++) {
        if (pos >= slides[i].offsetLeft && pos < slides[i].offsetLeft + slides[i].offsetWidth) {
          return i;
        }
      }
      return 0;
    }

    function syncThumbs() {
      if (!thumbs.length) return;
      var index = activeIndex();
      thumbs.forEach(function (t, i) {
        if (i === index) {
          t.setAttribute('aria-current', 'true');
        } else {
          t.removeAttribute('aria-current');
        }
      });
    }

    if (track) {
      var scrollRaf = null;
      track.addEventListener('scroll', function () {
        if (scrollRaf) return;
        scrollRaf = window.requestAnimationFrame(function () {
          scrollRaf = null;
          syncThumbs();
        });
      }, { passive: true });

      // Keyboard support on the gallery region
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); scrollToIndex(activeIndex() - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); scrollToIndex(activeIndex() + 1); }
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { scrollToIndex(i); });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollToIndex(activeIndex() - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollToIndex(activeIndex() + 1); });

    /* ----------------------- Automatic reveal ------------------------------ */

    /* The whole product is one joke: the pattern, then the message. Playing that
       turn once, unprompted, is the demo. It runs a single time, only while the
       gallery is on screen and untouched, and any interaction cancels it. */
    var revealDelay = parseInt(root.dataset.revealDelay || '0', 10);

    if (revealDelay > 0 && slides.length > 1 && !REDUCED_MOTION.matches) {
      var revealTimer = null;
      var revealDone = false;

      function cancelReveal() {
        revealDone = true;
        if (revealTimer) clearTimeout(revealTimer);
      }

      /* Any sign the visitor is driving the gallery themselves wins. */
      ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(function (evt) {
        if (track) track.addEventListener(evt, cancelReveal, { once: true, passive: true });
      });
      thumbs.forEach(function (t) { t.addEventListener('click', cancelReveal, { once: true }); });
      if (prevBtn) prevBtn.addEventListener('click', cancelReveal, { once: true });
      if (nextBtn) nextBtn.addEventListener('click', cancelReveal, { once: true });

      /* Measured directly instead of via IntersectionObserver: a horizontally
         scrolling flex track never reported a 0.5 ratio, so the observer sat
         silent and the reveal never ran. A rect check has no such surprise. */
      function galleryOnScreen() {
        if (!track) return false;
        var r = track.getBoundingClientRect();
        if (!r.height) return false;
        var visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
        return visible / r.height > 0.4;
      }

      revealTimer = setTimeout(function () {
        /* Still on the first slide, still visible, still untouched. */
        if (revealDone || activeIndex() !== 0 || !galleryOnScreen()) return;
        revealDone = true;
        scrollToIndex(1);
      }, revealDelay);
    }

    /* -------------------- Autoplay video pause control ---------------------- */

    var autoplayWrap = root.querySelector('[data-dla-autoplay]');
    if (autoplayWrap) {
      var video = autoplayWrap.querySelector('video');
      var pauseBtn = autoplayWrap.querySelector('[data-dla-pause]');

      var setPaused = function (paused) {
        if (!video || !pauseBtn) return;
        if (paused) {
          video.pause();
          pauseBtn.setAttribute('aria-pressed', 'true');
          pauseBtn.setAttribute('aria-label', 'Play video');
        } else {
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
          pauseBtn.setAttribute('aria-pressed', 'false');
          pauseBtn.setAttribute('aria-label', 'Pause video');
        }
      };

      // Respect prefers-reduced-motion: never autoplay for these visitors.
      if (REDUCED_MOTION.matches) setPaused(true);

      if (pauseBtn && video) {
        pauseBtn.addEventListener('click', function () {
          setPaused(!video.paused);
        });
      }
    }

    /* ------------------------- Checkout add-ons ----------------------------- */

    /* The native form posts a single variant. When an add-on is ticked we take
       over and post every line in one /cart/add call, then go to checkout. */
    var addonInputs = Array.prototype.slice.call(
      root.querySelectorAll('[data-dla-addon]')
    );

    if (form && addonInputs.length) {
      form.addEventListener('submit', function (e) {
        var chosen = addonInputs.filter(function (i) { return i.checked; });
        if (!chosen.length) return; // nothing extra → let the form post normally

        e.preventDefault();

        var items = [{
          id: Number(idInput.value),
          quantity: Number(qtyInput ? qtyInput.value : 1)
        }];
        chosen.forEach(function (i) {
          items.push({ id: Number(i.getAttribute('data-variant-id')), quantity: 1 });
        });

        if (atcBtn) atcBtn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('cart/add failed: ' + res.status);
            window.location.href = '/checkout';
          })
          .catch(function () {
            /* Never strand the buyer: fall back to the plain single-item post. */
            if (atcBtn) atcBtn.disabled = false;
            addonInputs.forEach(function (i) { i.checked = false; });
            form.submit();
          });
      });
    }

    /* --------------------- Mobile sticky add-to-cart bar -------------------- */

    if (stickyBar && atcBtn && 'IntersectionObserver' in window) {
      stickyBar.hidden = false;
      stickyBar.classList.remove('is-visible');

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-visible', pastButton);
        });
      }, { threshold: 0 });

      observer.observe(atcBtn);

      if (stickyAtc && form) {
        stickyAtc.addEventListener('click', function () {
          if (form.requestSubmit) {
            form.requestSubmit();
          } else {
            form.submit();
          }
        });
      }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-dla-product]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor support: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-dla-product]');
    if (root) {
      root.dataset.dlaInit = '';
      initSection(root);
    }
  });
})();
